'use client';

import { useEffect, useState } from 'react';

interface VisualWeatherEffectProps {
  type: 'rain' | 'snow' | 'sunshine' | 'lightning' | 'fog' | 'wind' | 'none';
  intensity: 'light' | 'medium' | 'heavy';
}

export default function VisualWeatherEffect({ type, intensity }: VisualWeatherEffectProps) {
  const [particles, setParticles] = useState<Array<{id: number, x: number, y: number, delay: number, size: number}>>([]);
  const [showLightning, setShowLightning] = useState(false);

  useEffect(() => {
    if (type === 'none') return;

    // Generate particles based on intensity
    const intensityMap = { light: 10, medium: 20, heavy: 40 };
    const count = intensityMap[intensity];

    const newParticles = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * -20,
      delay: Math.random() * 5,
      size: 0.6 + Math.random() * 0.6,
    }));

    setParticles(newParticles);
  }, [type, intensity]);

  // Lightning flash effect
  useEffect(() => {
    if (type !== 'lightning') return;

    const flashInterval = setInterval(() => {
      setShowLightning(true);
      setTimeout(() => setShowLightning(false), 150);
    }, 3000 + Math.random() * 4000);

    return () => clearInterval(flashInterval);
  }, [type]);

  if (type === 'none') return null;

  // Lightning flash
  if (type === 'lightning') {
    return (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: showLightning ? 'rgba(255, 255, 255, 0.8)' : 'transparent',
          transition: 'background 0.1s ease-out',
          pointerEvents: 'none',
          zIndex: 60,
        }}
      />
    );
  }

  // Sunshine rays
  if (type === 'sunshine') {
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 60, overflow: 'hidden' }}>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: '-10%',
              left: `${10 + i * 20}%`,
              width: '3px',
              height: '120%',
              background: 'linear-gradient(to bottom, rgba(255, 255, 200, 0) 0%, rgba(255, 255, 200, 0.5) 50%, rgba(255, 255, 200, 0) 100%)',
              transform: 'rotate(15deg)',
              animation: `sunrayMove ${8 + i * 2}s ease-in-out infinite`,
            }}
          />
        ))}
        <style jsx>{`
          @keyframes sunrayMove {
            0%, 100% { opacity: 0.4; transform: rotate(15deg) translateX(0); }
            50% { opacity: 0.7; transform: rotate(15deg) translateX(20px); }
          }
        `}</style>
      </div>
    );
  }

  // Fog effect
  if (type === 'fog') {
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 58, overflow: 'hidden' }}>
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              bottom: `${i * 30}%`,
              left: 0,
              width: '100%',
              height: '40%',
              background: 'rgba(240, 248, 255, 0.4)',
              filter: 'blur(30px)',
              animation: `fogDrift ${20 + i * 5}s linear infinite`,
              animationDelay: `${i * 3}s`,
            }}
          />
        ))}
        <style jsx>{`
          @keyframes fogDrift {
            0% { transform: translateX(-20%); }
            100% { transform: translateX(120%); }
          }
        `}</style>
      </div>
    );
  }

  // Rain, Snow, Wind particles
  const particleConfig = {
    rain: { icon: '💧', animation: 'rainFall', duration: '1.5s', color: '#87CEEB' },
    snow: { icon: '❄', animation: 'snowFall', duration: '4s', color: '#FFF' },
    wind: { icon: '〰', animation: 'windBlow', duration: '3s', color: '#87CEEB' },
  };

  const config = particleConfig[type as keyof typeof particleConfig] || particleConfig.rain;

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 59, overflow: 'hidden' }}>
      {particles.map((particle) => (
        <div
          key={particle.id}
          style={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            fontSize: `${particle.size * 1.2}rem`,
            color: config.color,
            opacity: 0.8,
            animation: `${config.animation} ${config.duration} linear ${particle.delay}s infinite`,
          }}
        >
          {config.icon}
        </div>
      ))}
      <style jsx>{`
        @keyframes rainFall {
          to { transform: translateY(110vh); opacity: 0; }
        }
        @keyframes snowFall {
          to { transform: translateY(110vh) rotate(360deg); opacity: 0; }
        }
        @keyframes windBlow {
          to { transform: translateX(110vw) translateY(20vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

